import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AIReview } from '../../common/models/types';
import { fetchAnalysisResult } from '../../api/aiReviewService';
import AIReviewPrintTemplate from '../../components/templates/AIReviewPrintTemplate/index';
import { COLOR_SYSTEM } from '../../components/design/design-system';

const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Red")?.[600]};
`;

const AIReviewPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [reviewData, setReviewData] = useState<AIReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReviewData = async () => {
      try {
        // SessionStorage에서 데이터 확인
        const sessionData = sessionStorage.getItem('print_review_latest');
        
        if (sessionData) {
          // SessionStorage에 데이터가 있으면 사용
          const parsedData = JSON.parse(sessionData);
          setReviewData(parsedData);
          setLoading(false);
        } else if (id) {
          // SessionStorage에 없으면 API에서 가져오기
          const response = await fetchAnalysisResult(id);
          if (response.error === 0 && response.data) {
            setReviewData(response.data);
          } else {
            setError(response.message || '리뷰 데이터를 불러오는데 실패했습니다.');
          }
          setLoading(false);
        } else {
          setError('리뷰 ID가 없습니다.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load review:', err);
        setError('리뷰 데이터를 불러오는데 실패했습니다.');
        setLoading(false);
      }
    };

    loadReviewData();
  }, [id]);

  // 창 닫힐 때 SessionStorage 정리
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('print_review_latest');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        리뷰 데이터를 불러오는 중...
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        {error}
      </ErrorContainer>
    );
  }

  if (!reviewData) {
    return (
      <ErrorContainer>
        리뷰 데이터를 찾을 수 없습니다.
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <AIReviewPrintTemplate review={reviewData} />
    </PageContainer>
  );
};

export default AIReviewPrintPage;